<template>
  <div class="mapping-entry">
    <div class="mapping-entry__source">
      <ak-input
        v-model="sourceValue"
        :type="sourceInput"
        :disabled="sourceReadOnly"
        v-bind="sourceInputProps"
        required
      />
    </div>
    <div class="mapping-entry__target">
      <ak-select
        v-if="!lockTarget.value"
        v-model="targetValue"
        :options="targetFieldsAvailable"
        group-values="entries"
        group-label="category"
        :group-select="false"
        track-by="value"
        label="label"
        placeholder="Select field"
        :max-height="140"
        required
      >
        <template #option="{ option }">
          <span v-if="option.$isLabel">{{ option.$groupLabel }}</span>
          <span v-else
            >{{ option.label }}
            <span v-show="option.type === 'custom'"> ({{ option.value }})</span>
          </span>
        </template>
        <template #singleLabel="{ option }">
          <span>{{ option.label }}</span>
          <span v-show="option.type === 'custom'"> ({{ option.value }})</span>
        </template>
      </ak-select>
      <ak-input
        v-else
        :model-value="targetValueLabel"
        :disabled="true"
        tabindex="-1"
      />
    </div>
    <div class="mapping-entry__actions">
      <ak-tooltip>
        <template #trigger>
          <ak-button
            v-show="!lockTarget.value"
            class="operations-list__action"
            color="grey-lightest"
            size="small"
            circle
            @click="removeMappingEntry"
          >
            <ak-icon v-show="!lockTarget.value" type="trash" color="danger" />
          </ak-button>
        </template>
        Delete entry
      </ak-tooltip>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue'

export default defineComponent({
  name: 'AkIntegrationSFTPCreateLeadMappingEntry',
  props: {
    mapping: {
      required: true,
      type: Object,
    },
    sourceInput: {
      default: 'text',
      type: String,
    },
    sourceInputProps: {
      required: true,
      type: Object,
    },
    sourceReadOnly: {
      default: false,
      type: Boolean,
    },
    targetFields: {
      required: true,
      type: Array,
    },
    targetFieldsAvailable: {
      required: true,
      type: Array,
    },
    lockTarget: {
      default: () => ({}),
      type: Object,
    },
  },
  emits: ['onUpdateMappingEntry', 'onDeleteMappingEntry'],
  setup(props, { emit }) {
    /**
     * Source value
     * @type {WritableComputedRef<*>}
     */
    const sourceValue = computed({
      get: () => props.mapping.ref,
      set: (value) =>
        emit('onUpdateMappingEntry', {
          mapping: { ...props.mapping, ref: value },
          debounced: true,
        }),
    })

    /**
     * Target value
     * @type {WritableComputedRef<T>}
     */
    const targetValue = computed({
      get: () =>
        props.targetFields
          .reduce(
            (categories, category) => categories.concat(category.entries),
            [],
          )
          .find((field) => field.value === props.mapping.config.field),
      set: (selected) =>
        selected &&
        emit('onUpdateMappingEntry', {
          mapping: {
            ...props.mapping,
            config: {
              field: selected.value,
              label: selected.label,
              type: selected.fieldType,
              scope: selected.scope,
            },
          },
        }),
    })

    /**
     * Target value label
     * @type {ComputedRef<unknown>}
     */
    const targetValueLabel = computed(
      () =>
        (targetValue.value && targetValue.value.label) ||
        props.mapping.config.field,
    )

    /**
     * Remove a mapping entry
     */
    const removeMappingEntry = () => emit('onDeleteMappingEntry')

    return {
      sourceValue,
      targetValue,
      targetValueLabel,
      removeMappingEntry,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@akerolabs/akero-vue/src/scss/_settings.scss';

.mapping-entry {
  display: grid;
  grid-template-columns: 1fr 1fr 20px;
  gap: $spacing-small;
  align-items: center;

  &__actions a {
    cursor: pointer;
  }
}
.mapping__entries {
  .mapping__entry-container:last-child {
    .mapping-entry__target {
      :deep(.ak-multiselect__content-wrapper) {
        bottom: 100% !important;
        border-top: 1px solid #ccc !important;
        border-bottom: none !important;
        border-radius: 5px 5px 0 0 !important;
      }
    }
  }
}
</style>
